import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip, Box, TextField, Button, CircularProgress } from '@mui/material';
import { JsonView, allExpanded } from 'react-json-view-lite';
import 'react-json-view-lite/dist/index.css';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import LoginIcon from '@mui/icons-material/Login';
import styles from './CompanyBadge.module.css';
import apiServiceCompany from '../../services/company.services';
import { useCompanyId } from '../../services/CompanyidProvider';
import logger from '../../utils/logger';

const CompanyBadge = ({ companyId }) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [newCompanyId, setNewCompanyId] = useState('');
    const [companyData, setCompanyData] = useState(null);
    const [copySuccess, setCopySuccess] = useState(false);
    const [isValid, setIsValid] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const { setCompanyId } = useCompanyId();

    const fetchCompanyData = useCallback(async (id) => {
        if (id && isValidCompanyId(id)) {
            setIsLoading(true);
            try {
                const response = await apiServiceCompany.getCompany(id);
                if (response.status === 'ok') {
                    setCompanyData(response.data);
                    setIsValid(true);
                } else {
                    setCompanyData(null);
                    setIsValid(false);
                }
            } catch (error) {
                setCompanyData(null);
                setIsValid(false);
            } finally {
                setIsLoading(false);
            }
        } else {
            setIsValid(false);
        }
    }, []);

    useEffect(() => {
        fetchCompanyData(companyId);
    }, [companyId, fetchCompanyData]);

    const isValidCompanyId = (id) => {
        return /^[0-9a-fA-F]{24}$/.test(id);
    };

    const handleOpenDialog = () => {
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setIsEditing(false);
        setNewCompanyId('');
    };

    const handleEditClick = () => {
        setIsEditing(true);
        setNewCompanyId(companyId);
    };

    const handleUpdateCompany = async () => {
        if (isValidCompanyId(newCompanyId)) {
            setCompanyId(newCompanyId);
            setIsEditing(false);
            await fetchCompanyData(newCompanyId);
        }
    };

    const handleCopyJson = () => {
        const jsonString = JSON.stringify(companyData, null, 2);
        navigator.clipboard.writeText(jsonString)
            .then(() => {
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 2000);
            })
            .catch(err => {
                logger.error('Failed to copy: ', err);
            });
    };

    const handleLoginToCompany = () => {
        if (companyData?._id) {
            const loginUrl = `https://app.meltwater.com/idp/tempLogin/${companyData._id}`;
            window.open(loginUrl, '_blank');
        }
    };

    if (!companyId || !isValid) {
        const hasNoCompany = !companyId;
        const badgeLabel = hasNoCompany ? 'Load a Company' : 'Invalid Company ID';
        const badgeClass = hasNoCompany ? styles.noCompanyBadge : styles.invalidBadge;
        return (
            <>
                <div className={badgeClass} onClick={handleOpenDialog}>
                    {badgeLabel}
                </div>

                <Dialog
                    open={isDialogOpen}
                    onClose={handleCloseDialog}
                    maxWidth="md"
                    fullWidth
                    classes={{ paper: styles.dialogPaper }}
                >
                    <DialogTitle className={styles.dialogTitle}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                            <Box sx={{ fontWeight: 'medium' }}>
                                {badgeLabel}
                            </Box>
                        </Box>
                    </DialogTitle>
                    <DialogContent className={styles.dialogContent}>
                        <Box className={styles.editContainer}>
                            <TextField
                                autoFocus
                                size="small"
                                label="Enter Valid Company ID"
                                value={newCompanyId}
                                onChange={(e) => setNewCompanyId(e.target.value)}
                                error={!isValidCompanyId(newCompanyId)}
                                helperText={!isValidCompanyId(newCompanyId) ? "Invalid Company ID format (24 character hex string)" : ""}
                                fullWidth
                                disabled={isLoading}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#188182',
                                        },
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: '#188182',
                                    },
                                }}
                            />
                            <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-start', width: '100%' }}>
                                <Button 
                                    onClick={handleUpdateCompany}
                                    variant="contained"
                                    className={styles.updateButton}
                                    disabled={!isValidCompanyId(newCompanyId) || isLoading}
                                >
                                    {isLoading ? <CircularProgress size={24} /> : 'Update'}
                                </Button>
                            </Box>
                        </Box>
                    </DialogContent>
                </Dialog>
            </>
        );
    }

    return (
        <>
            <div className={styles.companyBadge} onClick={handleOpenDialog}>
                Active Company: {companyData?.name || 'Loading...'}
            </div>

            <Dialog 
                open={isDialogOpen} 
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
                classes={{ paper: styles.dialogPaper }}
            >
                <DialogTitle className={styles.dialogTitle}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                        <Box sx={{ fontWeight: 'medium' }}>
                            Active Company: {companyData?.name || 'Loading...'}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Button
                                startIcon={<LoginIcon />}
                                onClick={handleLoginToCompany}
                                variant="contained"
                                className={styles.editButton}
                                disabled={!companyData?._id || isLoading}
                                size="small"
                            >
                                Log into MI
                            </Button>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent className={styles.dialogContent}>
                    <Box 
                        className={styles.jsonContainer}
                        sx={{
                            maxHeight: '60vh',
                            overflow: 'auto',
                            position: 'relative',
                            '&::-webkit-scrollbar': {
                                width: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                                background: '#f1f1f1',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: '#888',
                                borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-thumb:hover': {
                                background: '#555',
                            },
                            '&:hover .copy-button': {
                                opacity: 1,
                            },
                        }}
                    >
                        <Box 
                            className="copy-button"
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                opacity: 0,
                                transition: 'opacity 0.2s ease',
                                zIndex: 10,
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                backdropFilter: 'blur(4px)',
                                borderRadius: '4px',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                            }}
                        >
                            <Tooltip title={copySuccess ? "Copied!" : "Copy JSON"}>
                                <IconButton 
                                    onClick={handleCopyJson}
                                    size="small"
                                    className={styles.copyButton}
                                    sx={{
                                        padding: '6px',
                                        '&:hover': {
                                            backgroundColor: 'rgba(29, 159, 159, 0.08)',
                                        },
                                    }}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box className={styles.jsonWrapper}>
                            {isLoading ? (
                                <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <JsonView 
                                    data={companyData} 
                                    shouldInitiallyExpand={allExpanded}
                                />
                            )}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions className={styles.dialogActions}>
                    {!isEditing ? (
                        <Button
                            startIcon={<EditIcon />}
                            onClick={handleEditClick}
                            variant="outlined"
                            className={styles.cancelButton}
                            disabled={isLoading}
                        >
                            Edit Company ID
                        </Button>
                    ) : (
                        <Box className={styles.editContainer} sx={{ width: '100%' }}>
                            <TextField
                                autoFocus
                                size="medium"
                                label="New Company ID"
                                value={newCompanyId}
                                onChange={(e) => setNewCompanyId(e.target.value)}
                                error={!isValidCompanyId(newCompanyId)}
                                helperText={!isValidCompanyId(newCompanyId) ? "Invalid Company ID format (24 character hex string)" : "Enter a valid 24 character hex string"}
                                fullWidth
                                disabled={isLoading}
                                variant="outlined"
                                sx={{
                                    mb: 3,
                                    '& .MuiOutlinedInput-root': {
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#1d9f9f',
                                            borderWidth: '2px',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#1d9f9f',
                                        },
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: '#1d9f9f',
                                        fontWeight: 500,
                                    },
                                    '& .MuiFormHelperText-root': {
                                        fontSize: '0.875rem',
                                        marginTop: '8px',
                                    },
                                }}
                            />
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-start', width: '100%' }}>
                                <Button 
                                    onClick={handleUpdateCompany}
                                    variant="contained"
                                    className={styles.updateButton}
                                    disabled={!isValidCompanyId(newCompanyId) || isLoading}
                                    sx={{ minWidth: '140px' }}
                                >
                                    {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Update Company'}
                                </Button>
                                <Button 
                                    onClick={() => setIsEditing(false)}
                                    variant="outlined"
                                    className={styles.cancelButton}
                                    disabled={isLoading}
                                    sx={{ minWidth: '100px' }}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </Box>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CompanyBadge;
